import classNames from "classnames";
import { ComponentPropsWithoutRef, useEffect, useState } from "react";

import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropsWithDataQaid } from "src/types/componentProps";
import { PaginationItem, PaginationLink, PaginationList } from "./Pagination.styled";
import { getTranslationManually } from "src/helpers/locale";

export type PaginationProps = ComponentPropsWithoutRef<"div"> &
  PropsWithDataQaid<{
    /**
     * Set total data from all pages
     */
    totalData?: number;

    /**
     * Set data to be shown in one page
     */
    limit?: number;

    /**
     * Set current active page
     */
    currentPage?: number;

    /**
     * Callback after page has been changed
     * @param page - The new page number
     * @param nextCursorId - Cursor ID for next page (when useCursorId is true)
     * @param prevCursorId - Cursor ID for previous page (when useCursorId is true)
     */
    onPageChange: (page: number, nextCursorId?: number, prevCursorId?: number) => void;

    /**
     * set next navigation
     */
    next: number;

    /**
     * set previous navigation
     */
    previous: number;

    /**
     * set pagination without number link item of pages
     */
    noLinkItem: boolean;

    /**
     * condition to use cursor_id
     */
    useCursorId: boolean;
  }>;

const defaultProps = {
  totalData: 1,
  limit: 1,
  currentPage: 1,
  next: 0,
  previous: 0,
  onPageChange: () => {},
  noLinkItem: false,
  useCursorId: false
};

const Pagination = (props: PaginationProps) => {
  const {
    className,
    totalData,
    limit,
    currentPage,
    next,
    previous,
    noLinkItem,
    onPageChange,
    "data-qaid": dataQaid,
    useCursorId
  } = props;
  const paginationClass = classNames("c-pagination", className);

  const [totalPage, setTotalPage] = useState(Math.ceil(totalData / limit));
  const [activePage, setActivePage] = useState(currentPage);
  const [links, setLinks] = useState([]);

  function range(from: number, to: number, step = 1) {
    let i = from;
    const range = [];

    while (i <= to) {
      range.push(i);
      i += step;
    }
    return range;
  }

  function buildLinks(nextPage = currentPage) {
    const currPage = nextPage;
    const neighbours = 2;
    const totalNeighbours = neighbours * 2;

    if (totalPage < totalNeighbours + 4) {
      setLinks(range(1, totalPage));
    } else if (currPage < totalNeighbours + 1) {
      const pages = range(1, totalNeighbours + 3);
      setLinks([...pages, "...", totalPage]);
    } else if (currPage > totalPage - totalNeighbours) {
      const pages = range(totalPage - totalNeighbours - 2, totalPage);
      setLinks([1, "...", ...pages]);
    } else {
      const pages = range(currPage - neighbours, currPage + neighbours);
      setLinks([1, "...", ...pages, "...", totalPage]);
    }
  }

  function changePage(page: any) {
    if (page === "...") return;
    if (page === activePage) return;

    let newPage = page;
    let nextCursorId: number | undefined = useCursorId ? undefined : 0;
    let prevCursorId: number | undefined = useCursorId ? undefined : 0;

    if (page === "next") {
      if (useCursorId) {
        // Cursor-based: keep page as is, only set next_cursor_id (prev stays persistent)
        newPage = activePage;
        nextCursorId = next;
      } else {
        // Offset-based: increment page
        const condition = noLinkItem ? activePage : activePage < totalPage;
        newPage = condition ? activePage + 1 : totalPage;
      }
    } else if (page === "prev") {
      if (useCursorId) {
        // Cursor-based: keep page as is, only set prev_cursor_id (next stays persistent)
        newPage = activePage;
        prevCursorId = previous;
      } else {
        // Offset-based: decrement page
        newPage = activePage > 1 ? activePage - 1 : 1;
      }
    }

    setActivePage(newPage);
    buildLinks(newPage);
    onPageChange(newPage, nextCursorId, prevCursorId);
    window.scrollTo({ top: 0 });
  }

  useEffect(() => {
    buildLinks();
  }, [totalPage]);

  useEffect(() => {
    setTotalPage(Math.ceil(totalData / limit));
  }, [totalData]);

  useEffect(() => {
    if (currentPage && currentPage !== activePage) {
      changePage(currentPage);
    }
  }, [currentPage]);

  return (
    <PaginationList className={paginationClass} data-testid="qa-pagination" data-qaid={dataQaid}>
      {noLinkItem ? (
        <>
          <PaginationItem
            className={classNames("c-pagination__item", previous === 0 && "is-disabled", noLinkItem && "has-no-item")}
            data-testid="qa-pagination-previous-button"
          >
            <PaginationLink
              className="c-pagination__link"
              onClick={() => changePage("prev")}
              data-testid="qa-pagination-previous-button-link"
              data-qaid={`${dataQaid}-previous-button`}
            >
              <FontAwesomeIcon icon={faChevronLeft} data-testid="qa-pagination-previous-button-icon" />
              <span className="c-pagination__link-prev">{getTranslationManually("Sebelumnya", "Previous")}</span>
            </PaginationLink>
          </PaginationItem>

          <PaginationItem
            className={classNames("c-pagination__item", next === 0 && "is-disabled", noLinkItem && "has-no-item")}
            data-testid="qa-pagination-next-button"
          >
            <PaginationLink
              className="c-pagination__link"
              onClick={() => changePage("next")}
              data-testid="qa-pagination-next-button-link"
              data-qaid={`${dataQaid}-next-button`}
            >
              <span className="c-pagination__link-next">{getTranslationManually("Selanjutnya", "Next")}</span>
              <FontAwesomeIcon icon={faChevronRight} data-testid="qa-pagination-next-button-icon" />
            </PaginationLink>
          </PaginationItem>
        </>
      ) : (
        <>
          <PaginationItem
            className={classNames("c-pagination__item", activePage === 1 && "is-disabled")}
            data-testid="qa-pagination-previous-button"
          >
            <PaginationLink
              className="c-pagination__link"
              onClick={() => changePage("prev")}
              data-testid="qa-pagination-previous-button-link"
              data-qaid={`${dataQaid}-previous-button`}
            >
              <FontAwesomeIcon icon={faChevronLeft} data-testid="qa-pagination-previous-button-icon" />
            </PaginationLink>
          </PaginationItem>

          {links.map((page, index) => {
            const itemClass = classNames(
              "c-pagination__item",
              page === activePage && "is-current",
              page === "..." && "is-disabled"
            );
            return (
              <PaginationItem key={index} className={itemClass} data-testid={`qa-pagination-page-button-${page}`}>
                <PaginationLink
                  className="c-pagination__link"
                  onClick={() => changePage(page)}
                  data-testid={`qa-pagination-page-link-${page}`}
                  data-qaid={`${dataQaid}-page-${page}`}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          <PaginationItem
            className={classNames("c-pagination__item", activePage === totalPage && "is-disabled")}
            data-testid="qa-pagination-next-button"
          >
            <PaginationLink
              className="c-pagination__link"
              onClick={() => changePage("next")}
              data-testid="qa-pagination-next-button-link"
              data-qaid={`${dataQaid}-next-button`}
            >
              <FontAwesomeIcon icon={faChevronRight} data-testid="qa-pagination-next-button-icon" />
            </PaginationLink>
          </PaginationItem>
        </>
      )}
    </PaginationList>
  );
};

Pagination.defaultProps = defaultProps;
Pagination.displayName = "Pagination";

export default Pagination;
